import { ExportOutlined } from "@ant-design/icons";
import { ClickHouseSecretVault } from "@p0security/integrations-clickhouse-shared/components";
import {
  VAULT_OUTSIDE_CONNECTOR_CLOUD,
  renderHosting,
} from "@p0security/integrations-clickhouse-shared/hosting";
import {
  CLICKHOUSE_API_KEY_SYSTEM_ROLE,
  CLICKHOUSE_CONNECTOR_ROLE,
  CLICKHOUSE_REQUIRED_PERMISSIONS,
} from "@p0security/integrations-clickhouse-shared/permissions";
import type {
  ClickHouseConnectorHosting,
  ClickHouseOrganizationConfig,
} from "@p0security/integrations-clickhouse-shared/types";
import { shellLines } from "@p0security/integrations-vaulted-credential-shared";
import {
  Alert,
  Button,
  CommandDisplay,
  Flex,
  Steps,
  Typography,
} from "@p0security/ui";
import { assertNever } from "@p0security/util/assertNever";
import { type ComponentProps, Fragment, type ReactNode } from "react";

import { PLACEHOLDER_DOMAIN, toEmailDomain } from "./connector-iac.ts";
import type { ReadGrantBlock } from "./organization-iac.ts";

const { Paragraph, Text } = Typography;

type Vault = ClickHouseOrganizationConfig["vault"];

/** What an organization's page can show of its read grant. */
export type ReadGrant =
  | {
      status: "ready";
      /** The Terraform that lets the connector read the API key, in the
       * order to apply it */
      blocks: ReadGrantBlock[];
      hosting: ClickHouseConnectorHosting;
      /** The AWS account that holds the secret, when it isn't the
       * connector's */
      vaultAccountId: string | undefined;
    }
  /** The organization's connector isn't installed */
  | { status: "no-connector" }
  /** The secret isn't in the connector's cloud, so no Terraform grants it */
  | { status: "vault-outside-cloud"; hosting: ClickHouseConnectorHosting };

type Step = { content: ReactNode; status?: "error" | "finish"; title: string };

/** Whether a stored item has been set up: its checks have passed, and its
 * page shows the form, and its Terraform only on request. */
export const isSetUp = (item: { state?: string } | undefined) =>
  item?.state === "configure" || item?.state === "installed";

const CLICKHOUSE_CONSOLE_URL = "https://console.clickhouse.cloud/";

/** The permission that makes the key admin-equivalent. Typed as one of the
 * role's, so it can't name one the role doesn't hold. */
const MANAGE_PERMISSION: (typeof CLICKHOUSE_REQUIRED_PERMISSIONS)[number] =
  "control-plane:organization:manage";

/** The shape of the secret that holds an organization's API key, which the
 * connector parses strictly. */
const API_KEY_SECRET_SHAPE = '{"keyId": "...", "keySecret": "..."}';

/** The customer-managed KMS key for a secret in another account than the
 * connector's, in the command that creates the secret. */
const KMS_KEY_ARN = "<kms-key-arn>";

/** How each shell reads the API key without leaving it in the shell's
 * history, the key ID as it's pasted and the key secret without echoing it,
 * and then forgets it. Each read is one line, so that pasting it doesn't feed
 * the second read to the first. Fish has neither `read -r` nor `unset`: given
 * bash's line, it runs the pasted key secret as a command, and saves it in its
 * history. zsh's `read -p` reads from a coprocess, so bash's line has no
 * prompts. Each command that stores the key takes it from standard input, so
 * it's never an argument, which other users of the machine could list. */
const SHELLS = [
  {
    clearKey: "unset CLICKHOUSE_KEY_ID CLICKHOUSE_KEY_SECRET",
    name: "bash or zsh",
    readKey: "read -r CLICKHOUSE_KEY_ID && read -rs CLICKHOUSE_KEY_SECRET",
  },
  {
    clearKey: "set -e CLICKHOUSE_KEY_ID CLICKHOUSE_KEY_SECRET",
    name: "fish",
    readKey:
      "read -P 'Key ID: ' CLICKHOUSE_KEY_ID; and read -s -P 'Key secret: ' CLICKHOUSE_KEY_SECRET",
  },
] as const;

// Writes the key as the secret's JSON. printf is a builtin of every shell
// above, so the key isn't an argument of any process here either, and each of
// them runs the same line. A pasted value with a quote or a backslash in it
// makes JSON the connector refuses, which the install's secret check reports.
const renderKeyPipe = (...lines: string[]) =>
  `printf '{"keyId":"%s","keySecret":"%s"}' "$CLICKHOUSE_KEY_ID" "$CLICKHOUSE_KEY_SECRET" | ${shellLines(...lines)}`;

const toVaultLabel = (vault: Vault) =>
  ClickHouseSecretVault.options[vault.type].label;

/** How to store the API key as the organization's secret: where the secret
 * is, such as "account 123456789012 and region us-west-2", the command that
 * creates it, and the command for a secret that already exists. */
const toStoreInstructions = (
  { apiKeySecretName: name, vault }: ClickHouseOrganizationConfig,
  { crossAccount }: { crossAccount: boolean }
): { create: string; location: string; update: string } => {
  switch (vault.type) {
    case "aws-sm":
      return {
        create: renderKeyPipe(
          "aws secretsmanager create-secret",
          `--name ${name}`,
          ...(crossAccount ? [`--kms-key-id ${KMS_KEY_ARN}`] : []),
          "--secret-string file:///dev/stdin",
          `--region ${vault.secretsRegion}`
        ),
        location: `account ${vault.install} and region ${vault.secretsRegion}`,
        update: renderKeyPipe(
          "aws secretsmanager put-secret-value",
          `--secret-id ${name}`,
          "--secret-string file:///dev/stdin",
          `--region ${vault.secretsRegion}`
        ),
      };
    case "gcp-sm":
      return {
        create: renderKeyPipe(
          `gcloud secrets create ${name}`,
          `--project ${vault.install}`,
          "--data-file=-"
        ),
        location: `project ${vault.install}`,
        update: renderKeyPipe(
          `gcloud secrets versions add ${name}`,
          `--project ${vault.install}`,
          "--data-file=-"
        ),
      };
    default:
      throw assertNever(vault);
  }
};

/** Whether the connector's allowed-domain `pattern` lets it act for
 * `domain`, as the connector tests it: unanchored, against the part of an
 * email after the "@". A pattern that doesn't compile allows nothing. */
export const allowsDomain = ({
  domain,
  pattern,
}: {
  domain: string;
  pattern: string;
}) => {
  try {
    return new RegExp(pattern.trim()).test(domain);
  } catch {
    return false;
  }
};

/** How the page words each status in the organization's service status. The
 * stored value keeps the connector's tokens, which the Terraform provider
 * shows as they are. */
const SERVICE_STATUS_WORDS = [
  [": console-only (", ": console roles only ("],
  [": not-ready (", ": not ready ("],
] as const;

/** The organization's service status, `<name>: <status> (<reason>)` for each
 * service, with each status in words. */
const renderServiceStatus = (serviceStatus: string) =>
  SERVICE_STATUS_WORDS.reduce(
    (status, [token, words]) => status.replaceAll(token, words),
    serviceStatus
  );

/** Says how to create the `p0-connector` role, with the permissions the
 * connector's install check probes for. */
const RoleInstructions: React.FC<{ organizationId?: string }> = (props) => {
  const { organizationId } = props;
  return (
    <>
      <Text>
        In the ClickHouse Cloud console
        {organizationId === undefined
          ? ""
          : ` for organization ${organizationId}`}
        , create a custom role named{" "}
        <Text code>{CLICKHOUSE_CONNECTOR_ROLE}</Text> with exactly these
        permissions, and no others:
      </Text>
      <CommandDisplay commands={CLICKHOUSE_REQUIRED_PERMISSIONS.join("\n")} />
    </>
  );
};

/** Says which roles the API key holds. */
const KeyRoles: React.FC = () => (
  <>
    two roles: <Text code>{CLICKHOUSE_CONNECTOR_ROLE}</Text>, and the system
    role {CLICKHOUSE_API_KEY_SYSTEM_ROLE}, which running P0&apos;s Query API
    endpoint needs
  </>
);

/** Says how to create the role and the API key the connector acts as. The
 * new organization's form shows this, since the key must exist before its
 * secret can hold it. */
export const ConsoleInstructions: React.FC = () => (
  <Flex gap={8} vertical>
    <RoleInstructions />
    <Text>
      Then create an API key with <KeyRoles />. Keep its key ID and key secret
      at hand: the next page shows how to store them.
    </Text>
  </Flex>
);

const OpenConsole: React.FC = () => (
  <div>
    <Button
      href={CLICKHOUSE_CONSOLE_URL}
      icon={<ExportOutlined />}
      rel="noopener noreferrer"
      target="_blank"
    >
      Open the ClickHouse Cloud console
    </Button>
  </div>
);

const CreateRole: React.FC<{ organizationId: string }> = (props) => {
  const { organizationId } = props;
  return (
    <Flex gap={8} vertical>
      <RoleInstructions organizationId={organizationId} />
      <Text>
        <Text code>{MANAGE_PERMISSION}</Text> is admin-equivalent: it lets the
        key create roles and add anyone, itself included, to any role. The
        connector only ever changes or deletes roles whose names start with{" "}
        <Text code>p0_</Text>.
      </Text>
      <OpenConsole />
    </Flex>
  );
};

const CreateKey: React.FC = () => (
  <Text>
    Create an API key with <KeyRoles />. Copy its key ID and key secret when the
    console shows them.
  </Text>
);

const StoreKey: React.FC<{
  crossAccount: boolean;
  item: ClickHouseOrganizationConfig;
}> = ({ crossAccount, item }) => {
  const { apiKeySecretName: name, vault } = item;
  const { create, location, update } = toStoreInstructions(item, {
    crossAccount,
  });
  return (
    <Flex gap={8} vertical>
      <Text>
        Store the API key in {toVaultLabel(vault)} as the secret{" "}
        <Text code>{name}</Text>, in {location}, as JSON:{" "}
        <Text code>{API_KEY_SECRET_SHAPE}</Text>.{" "}
        {crossAccount && (
          <>
            The connector runs in another AWS account, so the secret needs a
            customer-managed KMS key: replace <Text code>{KMS_KEY_ARN}</Text>{" "}
            with that key&apos;s ARN.{" "}
          </>
        )}
        Run this first. Paste the key ID and press Enter, then paste the key
        secret and press Enter. It reads the key secret without showing it.
      </Text>
      {SHELLS.map(({ name, readKey }) => (
        <Fragment key={name}>
          <Text>In {name}:</Text>
          <CommandDisplay commands={readKey} />
        </Fragment>
      ))}
      <Text>
        Then create the secret, with the same command in any of these shells:
      </Text>
      <CommandDisplay commands={create} />
      <Text>
        If the secret already exists, store the API key as a new version
        instead:
      </Text>
      <CommandDisplay commands={update} />
      <Text>Then clear the API key from your shell.</Text>
      {SHELLS.map(({ clearKey, name }) => (
        <Fragment key={name}>
          <Text>In {name}:</Text>
          <CommandDisplay commands={clearKey} />
        </Fragment>
      ))}
      <Text>
        P0 never reads the API key. Only your connector does. To rotate it,
        create a new key with the same two roles, store it the same way as a new
        version of this secret, then delete the old key.
      </Text>
    </Flex>
  );
};

const toDeployedStep = (
  connectorId: string,
  hosting: ClickHouseConnectorHosting
): Omit<Step, "title"> => ({
  content: (
    <Text>
      Done: the connector {connectorId} runs as {renderHosting(hosting)}. It
      acts only for the email domains its deployment allows.
    </Text>
  ),
  status: "finish",
});

/** Says where to apply the Terraform that lets the connector read the API
 * key. */
const GrantRead: React.FC<{
  /** The AWS account that holds the secret, when it isn't the connector's */
  vaultAccountId: string | undefined;
}> = ({ vaultAccountId }) => (
  <Text>
    Apply the Terraform below. It lets the connector read this one secret, and
    no other.
    {vaultAccountId && (
      <>
        {" "}
        The secret and the connector are in different AWS accounts, so there are
        two configurations: one for the connector&apos;s account, and one for
        account {vaultAccountId}, which holds the secret.
      </>
    )}
  </Text>
);

/** What the steps that depend on the organization's connector show, for each
 * state of its read grant: the connector's own step, what the step that
 * grants the read says, and whether the secret is in another AWS account than
 * the connector. */
const toConnectorSteps = (
  connectorId: string,
  grant: ReadGrant
): { crossAccount: boolean; deploy: Omit<Step, "title">; read: ReactNode } => {
  switch (grant.status) {
    case "no-connector":
      return {
        crossAccount: false,
        deploy: {
          content: (
            <Alert
              message={`The connector ${connectorId} isn't installed. Install it again under Connector, with the same ID and hosting, or remove this organization and add it again with another connector.`}
              showIcon
              type="error"
            />
          ),
          status: "error",
        },
        read: <Text>Install the connector first.</Text>,
      };
    case "ready":
      return {
        crossAccount: grant.vaultAccountId !== undefined,
        deploy: toDeployedStep(connectorId, grant.hosting),
        read: <GrantRead vaultAccountId={grant.vaultAccountId} />,
      };
    case "vault-outside-cloud":
      return {
        crossAccount: false,
        deploy: toDeployedStep(connectorId, grant.hosting),
        read: (
          <Alert
            message={`${VAULT_OUTSIDE_CONNECTOR_CLOUD} Remove this organization and add it again.`}
            showIcon
            type="error"
          />
        ),
      };
    default:
      throw assertNever(grant);
  }
};

/** Where the organization's validation stands: on a staged organization,
 * what "Next" checks, and that "Finish" on the next page completes the
 * install. */
const ValidationStatus: React.FC<{
  item: ClickHouseOrganizationConfig;
  organizationId: string;
}> = ({ item, organizationId }) => {
  switch (item.state) {
    case "installed":
      return (
        <Text>
          P0 last validated this organization with the API key {item.apiKeyId}.
        </Text>
      );
    case "configure":
      return (
        <Text>
          The checks passed. Click &quot;Finish&quot; below to install the
          organization.
        </Text>
      );
    case "error":
    case "stage":
      return (
        <>
          <Text>
            Click &quot;Next&quot;. P0 asks the connector to read the API key,
            sign in to organization {organizationId} with it, check that the
            key&apos;s {CLICKHOUSE_CONNECTOR_ROLE} role has every permission
            above except{" "}
            <Text code>control-plane:service:manage-query-api-endpoints</Text>,
            and list the organization&apos;s services. If a check fails, P0 says
            which one. If the connector is denied the secret right after you
            apply the Terraform, wait a few seconds and click &quot;Next&quot;
            again.
          </Text>
          <Text>
            P0 sets up its read-only Query API endpoint,{" "}
            <Text code>p0_objects</Text>, on a service the first time someone
            requests SQL access to it or lists its objects. That&apos;s when P0
            first uses{" "}
            <Text code>control-plane:service:manage-query-api-endpoints</Text>:
            if the role lacks it, that request or listing fails with a message
            that names it.
          </Text>
          <Text>
            When the checks pass, click &quot;Finish&quot; on the next page.
            Access in the organization can&apos;t be requested until you do.
          </Text>
        </>
      );
    default:
      throw assertNever(item.state);
  }
};

/** Says where the organization's validation stands. Once the connector has
 * reported its allowed domains and the organization's services, it warns
 * when the domains leave out the admin's own, and lists the services that
 * aren't ready. */
const Validate: React.FC<{
  adminEmail: string | null | undefined;
  item: ClickHouseOrganizationConfig;
  organizationId: string;
}> = ({ adminEmail, item, organizationId }) => {
  const { allowedDomains, serviceStatus } = item;
  const domain = toEmailDomain(adminEmail);
  return (
    <Flex gap={8} vertical>
      <ValidationStatus item={item} organizationId={organizationId} />
      {allowedDomains &&
        domain &&
        !allowsDomain({ domain, pattern: allowedDomains }) && (
          <Alert
            message={`The connector allows the email domains ${allowedDomains}, which leaves out your own, ${domain}.`}
            showIcon
            type="warning"
          />
        )}
      {serviceStatus && (
        <Alert
          message={`As of the last validation, some services aren't ready for every request: ${renderServiceStatus(serviceStatus)}.`}
          showIcon
          type="warning"
        />
      )}
      <Text>
        After you rotate the API key or add a service, the next SQL request on
        each service moves P0&apos;s Query API endpoint there to the new key, or
        sets it up on the new service.
      </Text>
    </Flex>
  );
};

/**
 * The steps that install one ClickHouse Cloud organization, in the order the
 * customer takes them, shown above the organization's Terraform.
 *
 * The customer deploys the connector before adding the organization, since
 * the organization's form lists only installed connectors, so that step
 * shows as done, or as failed if someone has since removed the connector.
 * On a set-up organization, whose checks have passed, every other step shows
 * as done too, and "Validate" once the organization is installed.
 */
export const OrganizationSteps: React.FC<{
  /** The signed-in admin's email. The steps warn when the allowed domains
   * leave out its domain. */
  adminEmail: string | null | undefined;
  grant: ReadGrant;
  item: ClickHouseOrganizationConfig;
  /** The ClickHouse Cloud organization's ID */
  organizationId: string;
}> = ({ adminEmail, grant, item, organizationId }) => {
  const { crossAccount, deploy, read } = toConnectorSteps(
    item.connector,
    grant
  );
  const done = isSetUp(item) ? "finish" : undefined;
  const steps: Step[] = [
    {
      content: <CreateRole organizationId={organizationId} />,
      status: done,
      title: `Create the ${CLICKHOUSE_CONNECTOR_ROLE} role`,
    },
    { content: <CreateKey />, status: done, title: "Create the API key" },
    {
      content: <StoreKey crossAccount={crossAccount} item={item} />,
      status: done,
      title: `Store the API key in ${toVaultLabel(item.vault)}`,
    },
    { ...deploy, title: "Deploy the connector" },
    {
      content: read,
      status: grant.status === "ready" ? done : undefined,
      title: "Let the connector read the API key",
    },
    {
      content: (
        <Validate
          adminEmail={adminEmail}
          item={item}
          organizationId={organizationId}
        />
      ),
      status: item.state === "installed" ? "finish" : undefined,
      title: "Validate",
    },
  ];
  const items: ComponentProps<typeof Steps>["items"] = steps.map(
    ({ content, status, title }) => ({
      description: content,
      status: status ?? "process",
      title,
    })
  );
  return <Steps direction="vertical" items={items} />;
};

/** What to do with a connector's Terraform, shown above it. */
export const ConnectorHelp: React.FC<{
  /** The signed-in admin's email, whose domain the Terraform's allowed-domain
   * pattern starts as */
  adminEmail: string | null | undefined;
  connectorId: string;
  hosting: ClickHouseConnectorHosting;
}> = ({ adminEmail, connectorId, hosting }) => (
  <>
    <Paragraph>
      The Terraform below deploys the connector {connectorId} as{" "}
      {renderHosting(hosting)}, and lets P0 invoke it. The connector reaches
      ClickHouse Cloud over the internet, at{" "}
      <Text code>api.clickhouse.cloud</Text> and{" "}
      <Text code>queries.clickhouse.cloud</Text>, so it needs no VPC.
    </Paragraph>
    <Paragraph>
      Before you apply it, set <Text code>domain_allow_pattern</Text> to the
      email domains the connector may act for.{" "}
      {toEmailDomain(adminEmail)
        ? "P0 fills in your own email's domain as a start."
        : `P0 doesn't know your email's domain, so it fills in ${PLACEHOLDER_DOMAIN}, which matches nobody: replace it.`}{" "}
      The pattern lives only in your deployment: P0 can&apos;t change it, and
      shows the one deployed on each organization as &quot;Allowed
      domains&quot;.
    </Paragraph>
    <Paragraph>
      Then click &quot;Next&quot;, and P0 checks that it can invoke the
      connector. When it can, click &quot;Finish&quot; on the next page:
      organizations can&apos;t name the connector until you do. Each
      organization&apos;s own Terraform then lets the connector read that
      organization&apos;s API key.
    </Paragraph>
  </>
);
